// @flow
/* global window */

import * as React from 'react';

import { useDispatch } from 'react-redux';

import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  fetchInfos,
  selectIsFetchingInfos,
  selectIsLoadedInfos,
} from '@gandi/billing';
import {
  selectHasOwnerOnly,
  mergeCart,
  setPricesCartWidget,
  setCartReselleeId,
  setCartUUID,
  selectCanChooseResellee,
  selectCartProductOwner,
  selectIsFailureCartOwnerList,
  selectCartReselleeID,
  selectCartOwnerIsReseller,
  setCartOwnerIsReseller,
  setCartOwner,
} from '@gandi/cart';
import { selectCurrentId, selectPersonalId } from '@gandi/redux-sharing';

import { localeSelector, type TranslatorProps } from '@gandi/translate';

import { useCartSummaryContext } from '../../Layout/context/CartSummary';
import { useAppSelector } from '../../hooks/useAppSelector';
import {
  selectHasMissingOrgInfo,
  selectShopCurrent,
} from '../../modules/organization/selectors';
import type {
  FullState,
  PreFetchArgs,
  PreFetchReturn,
  WithRouterProps,
} from '../../types';
import { selectHasDomainTransfer } from '../Setup/modules/selectors';

import ActionsBlock from './components/ActionsBlock';
import BundleTypeWarning from './components/BundleTypeWarning';
import CorporateAlert from './components/CorporateAlert';
import IncompatibleBundleGrid from './components/IncompatibleBundleGrid';
import InvoiceBlock from './components/InvoiceBlock';
import OwnersBlock from './components/OwnersBlock';
import OwnershipDisclaimer from './components/OwnershipDisclaimer';
import {
  CUSTOMER,
  RESELLER,
  ORGANIZATION,
  type OwnerType,
} from './components/ResellerOwnerType';
import TaxAlert from './components/TaxAlert';
import { mustCartOwnerBeReseller } from './modules/helpers';
import {
  selectCartOwners,
  selectShouldRedirectToResellerOwnerId,
  selectResellerOwnerId,
  selectShouldRedirectToAccountForm,
  selectRedirectToNonMinimal,
  selectCanChooseProductOwner,
  selectCurrentOrganizationCanPay,
  selectCanChangeProductContacts,
} from './modules/selectors';

type Params = {
  cartId: string,
};

type Query = {
  guess?: string,
};

export type InjectedProps = {
  translator: TranslatorProps,
  ...WithRouterProps<Params>,
};

export type Props = {
  ...InjectedProps,
};

function getDefaultOwnerType(
  chooseResellee: boolean,
  sharingId: string,
  resellee: ?string,
  owner: ?string,
  isOwnerReseller?: boolean,
  hasDomainTransfer?: boolean,
) {
  if (!chooseResellee) {
    return ORGANIZATION;
  }

  if (!resellee && owner && owner !== sharingId) {
    return ORGANIZATION;
  }

  if (isOwnerReseller && !hasDomainTransfer) {
    return RESELLER;
  }

  return CUSTOMER;
}

function Presenter(): React.MixedElement {
  const navigate = useNavigate();
  const { cartId } = useParams<{ cartId: string }>();
  const [searchParams] = useSearchParams();
  const {
    frozen,
    handleConfigurationClick,
    setFrozen,
    ownerType,
    setOwnerType,
    updateSharingIdContext,
  } = useCartSummaryContext();

  const dispatch = useDispatch();

  const currentOrg = useAppSelector(state => selectShopCurrent(state, true));
  const sharingId = currentOrg?.id;

  const billingProps = { sharingId };

  const fetchingBillingInfo = useAppSelector(state =>
    selectIsFetchingInfos(state, billingProps),
  );
  const loadedBillingInfo = useAppSelector(state =>
    selectIsLoadedInfos(state, billingProps),
  );

  const personalId = useAppSelector(selectPersonalId);
  const missingCartOwnerInfo = useAppSelector(
    state => !!selectHasMissingOrgInfo(state, sharingId) && !!sharingId,
  );

  const chooseResellee = useAppSelector(
    state => !!currentOrg?.reseller && selectCanChooseResellee(state),
  );
  const chooseOwner = useAppSelector(selectCanChooseProductOwner);

  const hasDomainTransfer = useAppSelector(selectHasDomainTransfer);

  const productOwner = useAppSelector(selectCartProductOwner);
  const missingProductOwnerInfo = useAppSelector(
    state => !!selectHasMissingOrgInfo(state, productOwner) && !!productOwner,
  );

  const failureCartOwners = useAppSelector(
    state => !!selectIsFailureCartOwnerList(state),
  );
  const cartOwners = useAppSelector(selectCartOwners);
  const canPay = useAppSelector(selectCurrentOrganizationCanPay);
  const resellee = useAppSelector(
    state => selectCartReselleeID(state) || undefined,
  );

  const ownerOnly = useAppSelector(selectHasOwnerOnly);

  const redirectToAccountForm = useAppSelector(
    selectShouldRedirectToAccountForm,
  );

  // $FlowFixMe PartialOrganization should be compatible with CartOwnerCandidate
  const ownerMustReseller = mustCartOwnerBeReseller(ownerOnly, cartOwners);
  const redirectToReseller = useAppSelector(
    selectShouldRedirectToResellerOwnerId,
  );
  const resellerOwner = useAppSelector(selectResellerOwnerId);
  const isOwnerReseller = useAppSelector(selectCartOwnerIsReseller);

  const redirectToNonMinimal = useAppSelector(selectRedirectToNonMinimal);

  const canChangeProductContacts = useAppSelector(
    selectCanChangeProductContacts,
  );
  const handleChangeOwnerType: (currentOwnerType: OwnerType | null) => void =
    React.useCallback(
      currentOwnerType => {
        if (ownerType !== currentOwnerType) {
          setOwnerType(currentOwnerType);

          if (currentOwnerType === RESELLER) {
            dispatch(setCartOwnerIsReseller(true));
          }
        }
      },
      [ownerType, dispatch, setOwnerType],
    );

  const handleRedirectToAccountForm = React.useCallback(() => {
    navigate(`/cart/${cartId}/configure/account/myself?first`, {
      replace: true,
    });
  }, [cartId, navigate]);

  React.useEffect(() => {
    // Workaround as not implementend in JSDOM for tests
    if (process.env.NODE_ENV !== 'test') {
      window.scrollTo(0, 0);
    }
    setFrozen(false);
    // Alway fetch the current org's prepaid info
    // Necessary to:
    // - ensure current cart owner has a currency set
    // - check owner's grid against bundle grid limitations if any.
    const fetchAPI = async () => {
      if (!fetchingBillingInfo && !loadedBillingInfo) {
        await dispatch(fetchInfos());
      }
    };

    fetchAPI();

    if (redirectToAccountForm) {
      handleRedirectToAccountForm();
    }

    if (redirectToReseller) {
      updateSharingIdContext({ newSharingId: resellerOwner });
    }

    if (!searchParams.has('guess') && redirectToNonMinimal) {
      updateSharingIdContext({
        newSharingId: redirectToNonMinimal,
        guess: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (redirectToAccountForm) {
      handleRedirectToAccountForm();
    }

    if (redirectToReseller) {
      updateSharingIdContext({ newSharingId: resellerOwner });
    }

    if (!frozen && !searchParams.has('guess') && redirectToNonMinimal) {
      updateSharingIdContext({
        newSharingId: redirectToNonMinimal,
        guess: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    handleRedirectToAccountForm,
    redirectToAccountForm,
    redirectToReseller,
    redirectToNonMinimal,
    frozen,
    searchParams,
    updateSharingIdContext,
  ]);

  React.useEffect(() => {
    dispatch(setPricesCartWidget(false));
    if (currentOrg) {
      handleChangeOwnerType(
        getDefaultOwnerType(
          chooseResellee,
          currentOrg.id,
          resellee,
          productOwner,
          isOwnerReseller,
          hasDomainTransfer,
        ),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    chooseResellee,
    currentOrg?.id,
    resellee,
    productOwner,
    currentOrg?.reseller,
    hasDomainTransfer,
  ]);

  function handleOwnerChange(orgId: string): void {
    if (currentOrg && currentOrg.reseller) {
      dispatch(setCartReselleeId(null));
    }

    updateSharingIdContext({
      newSharingId: orgId,
      guess: searchParams.has('guess') || orgId === personalId,
    });
  }

  const disabled =
    frozen ||
    !canPay ||
    !currentOrg ||
    (ownerMustReseller && !currentOrg.reseller) ||
    (!!(chooseResellee && !chooseOwner && currentOrg.reseller) && !resellee) ||
    !!(currentOrg.reseller && ownerType === CUSTOMER && !resellee);

  const missingInfo = missingCartOwnerInfo || missingProductOwnerInfo;

  return (
    <div>
      <InvoiceBlock
        ownerMustReseller={ownerMustReseller}
        failureCartOwners={failureCartOwners}
        onOwnerChange={handleOwnerChange}
        frozen={frozen}
      />
      {(chooseOwner || chooseResellee) && (
        <OwnersBlock
          key={currentOrg?.id}
          chooseOwner={chooseOwner}
          chooseResellee={chooseResellee}
          setOwnerType={handleChangeOwnerType}
        />
      )}
      <OwnershipDisclaimer />

      <CorporateAlert />
      <IncompatibleBundleGrid />
      <BundleTypeWarning />
      {productOwner && productOwner !== currentOrg?.id && <TaxAlert />}

      <ActionsBlock
        driveUserToSetup={missingInfo || hasDomainTransfer}
        canChangeSetup={canChangeProductContacts}
        disabled={disabled}
        onClickToSetup={handleConfigurationClick}
      />
    </div>
  );
}

Presenter.handleRedirect = async (
  args: PreFetchArgs<Props, Params, Query, FullState>,
) => {
  const { dispatch, getState } = args;

  dispatch(setCartUUID(args.params.cartId));
  await InvoiceBlock.preFetch(args);

  // Warning: Do not use injected props here.
  // Re-compute the result of these selectors based on the state update
  // of previous higher order actions.

  const redirectToAccountForm = selectShouldRedirectToAccountForm(getState());

  let redirectToReseller = selectShouldRedirectToResellerOwnerId(getState());
  const resellerOwner = selectResellerOwnerId(getState());
  redirectToReseller = !!(redirectToReseller && resellerOwner);

  const { query } = args;
  const redirectToNonMinimal =
    !query.guess && selectRedirectToNonMinimal(getState());

  const locale = localeSelector(getState());

  if (redirectToAccountForm || redirectToReseller || redirectToNonMinimal) {
    const {
      params: { cartId },
    } = args;

    let redirectTo = '';
    if (redirectToAccountForm) {
      // Set the Cart Owner in a lite, fire-and-forget fashion.
      dispatch(setCartOwner({ headers: { 'X-Dry-Run': 0 } })).catch(() => {});

      const sharingId = selectCurrentId(getState());
      redirectTo = `/${locale}/${sharingId}/cart/${cartId}/configure/account/myself?first`;
    } else if (redirectToReseller) {
      redirectTo = `/${locale}/${resellerOwner}/cart/${cartId}/configure/account-choice`;
    } else if (redirectToNonMinimal) {
      redirectTo = `/${locale}/${redirectToNonMinimal}/cart/${cartId}/configure/account-choice?guess`;
    }

    args.res.redirect(global.__DEV__ ? `/shop${redirectTo}` : redirectTo);
    throw new Error(`Redirect to ${redirectTo}`);
  }
};

Presenter.preFetch = async (
  args: PreFetchArgs<Props, Params, Query, FullState>,
): PreFetchReturn<> => {
  const { dispatch, getState, params } = args;

  dispatch(setCartUUID(params.cartId));
  // Set the Cart Owner in a lite, fire-and-forget fashion.
  dispatch(setCartOwner({ headers: { 'X-Dry-Run': 0 } })).catch(() => {});
  // FIXME: `setCartOwner` is inconvenient to use in "fire-and-forget fashion"
  // because, even in lite mode it updates cart.isWorking flag which is used
  // by other components such as the CartWidget.
  dispatch(mergeCart({ isWorking: false }));

  const org = selectShopCurrent(getState(), true);

  const chooseResellee = org?.reseller && selectCanChooseResellee(getState());
  const chooseOwner = selectCanChooseProductOwner(getState());

  return Promise.allSettled([
    // $FlowFixMe[incompatible-call]
    CorporateAlert.preFetch(args),
    // $FlowFixMe[incompatible-call]
    (chooseResellee || chooseOwner) && OwnersBlock.preFetch(args),
  ]);
};

export default Presenter;
